import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GoogleLogin } from '@react-oauth/google';
import { authenticate, login, getMqttCredentials, getWecs } from './server';
import { useMQTTConnect } from './hooks/useMqtt';
import { useDenshiState } from './hooks/useDenshiState';
import { useAnchor } from './hooks/useAnchor';
import { HOSTNAME, PI_HOSTNAME, SOURCES } from './lib/constants';
import { COLORS, monoLabel } from './lib/theme';
import Cam0Overlay from './components/Hud/Cam0Overlay';
import PositionPlate from './components/Hud/PositionPlate';
import CameraFeed from './components/CameraFeed/CameraFeed';

// The bridge polls the controller once a second and publishes whatever
// moved, so half a minute without anything from it means the road is closed.
const BRIDGE_FRESH_MS = 30_000;

const Page = styled.div`
  height: 100%;
  background: ${COLORS.ground};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 900px) {
    height: auto;
    min-height: 100%;
  }
`;

// The live view fills the viewport edge to edge -- no max-width, no padding.
const Stage = styled.div`
  width: 100%;
  height: 100%;

  @media (max-width: 900px) {
    height: auto;
  }
`;

const LoginBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  ${monoLabel}
  font-size: 10px;
  color: ${COLORS.text};
`;

const ErrorText = styled.div`
  ${monoLabel}
  font-size: 9px;
  color: ${COLORS.alert};
  max-width: 40em;
  text-align: center;
  line-height: 1.6;
`;

export default function App() {
  // Read-only: Google SSO -> flask-server -> /get_mqtt_credentials, the same
  // flow weco-dashboard uses. Nothing in this app ever publishes to MQTT.
  const [loggedIn, setLoggedIn] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [credentials, setCredentials] = useState(null);
  const [platformName, setPlatformName] = useState(null);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        await login();
        setLoggedIn(true);
      } catch (e) {
        setLoggedIn(false);
      }
      setCheckingSession(false);
    })();
  }, []);

  useEffect(() => {
    if (!loggedIn) return;

    (async () => {
      try {
        setCredentials(await getMqttCredentials());
      } catch (e) {
        console.error('Failed to get MQTT credentials', e);
      }
    })();

    (async () => {
      try {
        const wecs = await getWecs();
        const wec = wecs.find((w) => w.hostname === HOSTNAME);
        setPlatformName(wec?.name || HOSTNAME);
      } catch (e) {
        console.error('Failed to get WEC list', e);
        setPlatformName(HOSTNAME);
      }
    })();
  }, [loggedIn]);

  const {
    onMessage,
    fields,
    history,
    hourly,
    rates,
    latestReceivedAt,
    latestTimeSource,
    lastHeard,
    nowMs,
  } = useDenshiState(SOURCES);
  const { client: mqttClient, status: mqttStatus } = useMQTTConnect(credentials, onMessage);
  const { anchor, setAnchor, reset: resetAnchor } = useAnchor();

  // The controller's own status is the verdict when it is fresh. When that
  // link goes quiet, its readings may still be arriving through the Pi's
  // bridge (denshi-pi) -- that is 'bridge'. Failing that, the Pi's own status
  // (denshi-camera) says only that the unit is up and reachable. See
  // lib/link.js for what each state means on screen.
  const linkState = useMemo(() => {
    if (mqttStatus !== 'Connected') {
      return lastHeard.denshi || lastHeard.bridge || lastHeard.pi ? 'stale' : 'nodata';
    }

    const fresh = (f) => f.value === true && !f.stale;
    const bridgeAgeMs = lastHeard.bridge ? nowMs - lastHeard.bridge.getTime() : null;

    if (fresh(fields.connected) && fields.connected.via === 'denshi') return 'live';
    if (bridgeAgeMs != null && bridgeAgeMs <= BRIDGE_FRESH_MS) return 'bridge';
    if (fresh(fields.piConnected)) return 'pi';
    if (lastHeard.denshi || lastHeard.bridge || lastHeard.pi) return 'stale';
    return 'nodata';
  }, [fields.connected, fields.piConnected, lastHeard, nowMs, mqttStatus]);

  async function onGoogleLoginSuccess(response) {
    setAuthError(null);
    try {
      await authenticate(response.credential);
      setLoggedIn(true);
    } catch (e) {
      console.error('Google authentication failed', e);
      setAuthError(
        `Could not reach the server at ${process.env.REACT_APP_SERVER_URL || 'http://localhost:5000'} ` +
          `to complete sign-in (${e.message}). Is flask-server running and REACT_APP_SERVER_URL set correctly?`
      );
    }
  }

  // While the /login session check is in flight, render nothing that would
  // fire authenticated requests (get_picture_list, MQTT subscribe) before we
  // know whether the user is actually logged in.
  if (checkingSession) {
    return (
      <Page>
        <LoginBox>Checking session...</LoginBox>
      </Page>
    );
  }

  if (!loggedIn) {
    return (
      <Page>
        <LoginBox>
          <img src="/weco-logo-white.svg" alt="WECO" style={{ width: 160 }} />
          <div>Sign in to view the live DENSHI HUD overlay</div>
          <GoogleLogin
            onSuccess={onGoogleLoginSuccess}
            onError={() => setAuthError('Google sign-in itself failed -- check the console for details.')}
          />
          {authError && <ErrorText>{authError}</ErrorText>}
        </LoginBox>
      </Page>
    );
  }

  return (
    <Page>
      <Stage>
        <CameraFeed
          mqttClient={mqttClient}
          cameraHostname={PI_HOSTNAME}
          fields={fields}
          linkState={linkState}
          rates={rates}
          history={history}
          hourly={hourly}
          nowMs={nowMs}
          landscapeOverlay={
            <PositionPlate
              fields={fields}
              anchor={anchor}
              onAnchorChange={setAnchor}
              onAnchorReset={resetAnchor}
              hostname={HOSTNAME}
              nowMs={nowMs}
            />
          }
        >
          <Cam0Overlay
            platformName={platformName || HOSTNAME}
            latestReceivedAt={latestReceivedAt}
            latestTimeSource={latestTimeSource}
            lastHeard={lastHeard}
            nowMs={nowMs}
            linkState={linkState}
          />
        </CameraFeed>
      </Stage>
    </Page>
  );
}
